import type React from "react";
import { Calendar, ChevronDown, X } from "lucide-react";
import { useFilters } from "../context/FilterContext";

interface FiltersProps {
  filterType?: string;
}

const Filters: React.FC<FiltersProps> = ({ filterType = "all" }) => {
  const {
    startDate,
    endDate,
    year,
    selectedFilter,
    isFilterOpen,
    isYearOpen,
    setStartDate,
    setEndDate,
    setYear,
    setSelectedFilter,
    toggleFilterOpen,
    toggleYearOpen,
    handleApplyFilters,
    handleClearFilters,
    filterOptions,
    years,
  } = useFilters();

  const gridColsClass =
    filterType === "all"
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex flex-col gap-6">
        <div className={`grid ${gridColsClass} gap-6`}>
          {/* Start Date */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* End Date */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* Year Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={toggleYearOpen}
                className="w-full flex items-center justify-between pl-3 pr-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <span>{year}</span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {isYearOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                  <ul className="py-1 max-h-60 overflow-auto">
                    {years.map((yearOption) => (
                      <li key={yearOption}>
                        <button
                          type="button"
                          onClick={() => {
                            setYear(yearOption);
                            toggleYearOpen();
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none"
                        >
                          {yearOption}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {filterType === "all" && (
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter By
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={toggleFilterOpen}
                  className="w-full flex items-center justify-between pl-3 pr-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <span>
                    {selectedFilter
                      ? filterOptions.find(
                          (option) => option.value === selectedFilter
                        )?.label
                      : "Select metric"}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {isFilterOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                    <ul className="py-1 max-h-60 overflow-auto">
                      {filterOptions.map((option) => (
                        <li key={option.value}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFilter(option.value);
                              toggleFilterOpen();
                            }}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none"
                          >
                            {option.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={handleClearFilters}
            className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <X className="w-4 h-4 mr-2" />
            Clear
          </button>
          {/* <button
            onClick={handleApplyFilters}
            className="flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Apply Filters
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default Filters;
